import * as moment from 'moment';
import { Order } from 'src/app/shared/models/orders';

export function transformViajeDate(inputDate: string): string {
  const parsedDate = moment(inputDate, 'DD-MMM-YY');
  return parsedDate.format('DD/MM/YYYY');
}

function pickExisting(
  item: Record<string, unknown>,
  ...keys: string[]
): unknown {
  for (const key of keys) {
    if (item[key] !== undefined && item[key] !== null) {
      return item[key];
    }
  }
  return undefined;
}

function pickRemark(item: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const value = item[key];
    if (value != null && String(value) !== '') {
      return String(value);
    }
  }
  const fallback = pickExisting(item, ...keys);
  return fallback == null ? '' : String(fallback);
}

function parseAdditionFlag(
  item: Record<string, unknown>,
  ...keys: string[]
): boolean | undefined {
  const raw = pickRemark(item, ...keys);
  if (raw === '' && pickExisting(item, ...keys) === undefined) return undefined;
  const value = raw;
  if (value === '' || value === 'false') return false;
  if (value === 'true') return true;
  return undefined;
}

/** Firma desde campos legacy del API (VIAJESH). */
export function orderIsSignedFromApi(item: {
  MX_CLI_FIRMA?: string;
  MX_CLIENTE_FIRMA_H?: string;
}): boolean {
  const firma = item.MX_CLI_FIRMA ?? '';
  const hasFirmaContent = firma !== '' && firma !== '0';
  return hasFirmaContent || String(item.MX_CLIENTE_FIRMA_H ?? '') === '1';
}

/** Mapea envelope legacy `{ datos: [{ VIAJESH }] }` a modelos Order. */
export function mapOrdersFromApi(data: unknown): Order[] {
  const viajes = (data as { datos?: Array<{ VIAJESH?: unknown[] }> })?.datos?.[0]
    ?.VIAJESH;
  if (!Array.isArray(viajes)) {
    return [];
  }
  return viajes.map((item: Record<string, unknown>) => {
    const order = {
      tkt: item['TKT_H'] as number,
      deliveryDate: String(item['FECHA_PED_DATE_H'] ?? ''),
      orderDate: transformViajeDate(String(item['FECHA_PED_H'] ?? '')),
      plant: item['PLANTA_H'] as string,
      orderNumber: item['NUMERO_PEDID_H'] as number,
      clientCode: item['CODIGO_CLI_H'] as string,
      clientName: item['NOMBRE_CLIENTE_H'] as string,
      workCode: item['CODIGO_PROYE_H'] as string,
      workName: item['NOMBRE_PROYECTO_H'] as string,
      deliveryAddress: item['DIRECCION_ENTREGA_H'] as string,
      productCode: item['CODIGO_PRODU_H'] as number,
      productDescription: item['DESCRIPCION_PRODUCTO_H'] as string,
      scheduledAmount: item['CANTIDAD_PROGRAMADA_H'] as number,
      loadAmount: item['CARGAS_H'] as number,
      frequency: item['FRECUENCIA_H'] as number,
      ticketPrintingTime: item['IMPRESO_H'] as string,
      startTime: item['HORA_INI_H'] as string,
      estimatedTravelTime: item['TIEMPO_DE_VIAJE_H'] as string,
      requiredTrucks: item['CAMIONES_REQUERIDOS_H'] as number,
      loadSize: item['TAMANO_CARGA_H'] as number,
      quantityShipped: item['CANTIDAD_DESPACHADA_H'] as number,
      outstandingAmount: item['CANTIDAD_PENDIENTE_H'] as number,
      OnsiteContactPhoneNumber: item['TELEFONO_H'] as string,
      OnsiteContactName: item['CONTACTO_H'] as string,
      truckNumber: item['DESIGNATION_H_ANT'] as number,
      driverName: item['CONDUCTOR_H'] as string,
      truckPatent: item['PATENTE_H'] as string,
      currentTripNumber: item['NROCARGA_H'] as number,
      status: +String(item['STATUS_H'] ?? 0),
      webOrderNumber: item['ORDL_H'] as number,
      hourOne: item['HORA_UNO_H'] as string,
      hourTwo: item['HORA_DOS_H'] as string,
      hourThree: item['HORA_TRES_H'] as string,
      hourFour: item['HORA_CUATRO_H'] as string,
      hourFive: item['HORA_CINCO_H'] as string,
      hourSix: item['HORA_SEIS_H'] as string,
      hourSeven: item['HORA_SIETE_H'] as string,
      hourEight: item['HORA_OCHO_H'] as string,
      folioNumberGD: item['FOLIO_H'] as number,
      stateDescription: item['ESTADO'] as string,
      rejectionFlag: (item['MX_MOTIVO_RECHAZO'] as string) ?? '',
      isSignature: orderIsSignedFromApi(item as { MX_CLI_FIRMA?: string; MX_CLIENTE_FIRMA_H?: string }),
      openedQrLink: String(pickExisting(item, 'ABRIO_LINK_QR') ?? ''),
      openedQrAt: String(pickExisting(item, 'FECHA_ABRIO_LINK_QR') ?? ''),
      signedAt: String(pickExisting(item, 'FECHA_FIRMA') ?? ''),
      clientSignature: String(pickExisting(item, 'MX_CLI_FIRMA') ?? ''),
      signerRut: String(pickExisting(item, 'MX_CLI_FIRMA_RUT') ?? ''),
      signerName: String(pickExisting(item, 'MX_CLI_FIRMA_NOMBRE') ?? ''),
      signerEmail: String(pickExisting(item, 'MX_CLI_FIRMA_CORREO') ?? ''),
      signerComment: String(pickExisting(item, 'MX_COMENTARIOS_FIRMA') ?? ''),
      signerEmailDos: String(
        pickExisting(item, 'MX_CLI_FIRMA_CORREO_DOS', 'mx_cli_firma_correo_dos') ?? ''
      ),
      isOpen: false,
      concreteReturn: item['MX_COMENTARIOS_HORMI'] as string,
      commentsStructure: String(pickExisting(item, 'MX_COMENTARIOS_ESTRUCT') ?? ''),
      commentsCycles: String(pickExisting(item, 'MX_COMENTARIOS_CICLOS') ?? ''),
      commentsFinal: String(pickExisting(item, 'MX_COMENTARIOS_FINAL') ?? ''),
      generalComment: String(pickExisting(item, 'MX_COMENTARIO') ?? ''),
      waterAddition: parseAdditionFlag(
        item,
        'MX_ADICIONAL_UNO_H',
        'MX_ADICIONAL_UNO_CLI',
        'MxAdicionalUnoCli'
      ),
      waterAdditionConfirmed: parseAdditionFlag(item, 'MX_ADICIONAL_UNO_H') === true,
      waterSignerRut: String(pickExisting(item, 'MX_AGUA_FIRMA_RUT') ?? ''),
      waterSignerName: String(pickExisting(item, 'MX_AGUA_FIRMA_NOMBRE') ?? ''),
      waterSignerComment: String(pickExisting(item, 'MX_AGUA_COMENTARIO') ?? ''),
      returnSignerRut: String(pickExisting(item, 'MX_DEV_FIRMA_RUT') ?? ''),
      returnSignerName: String(pickExisting(item, 'MX_DEV_FIRMA_NOMBRE') ?? ''),
      returnSignerComment: String(pickExisting(item, 'MX_DEV_COMENTARIO') ?? ''),
      anotherAddition: parseAdditionFlag(
        item,
        'MX_ADICIONAL_DOS_H',
        'MX_ADICIONAL_DOS_CLI',
        'MxAdicionalDosCli'
      ),
      guidingRemarks: pickRemark(
        item,
        'MX_ADICIONAL_TRES_H',
        'MX_ADICIONAL_TRES_CLI',
        'MxAdicionalTresCli'
      ),
      routePdfGuide: item['PDF_GUIA_H'] as string,
      signedRoutePdfGuide: String(item['PDF_GUIA_H_F'] ?? ''),
      instructions: item['INSTRUCCIONES'] as string,
      additiveRemarks: item['OBSERVACIONES_ADIT'] as string,
      sealNumber: item['SELLO'] as string,
      cone: item['CONO'] as string,
      reasonToReturn: item['MX_MOTIVO_RETORNO'] as string,
      m3Returned: item['MX_M3_RETORNO'] !== '' ? item['MX_M3_RETORNO'] : '',
      samples: item['MUESTRAS'] as string,
      loadingMouth: item['BOCA_CARGA'] as string,
      urlMap: item['URLMAP'] as string,
      actualQuantityShipped: +String(item['CANT_DESP_REAL_H'] ?? 0),
      isDigitalSignature: item['ES_GUIA_DIGITAL'] == '1',
      isNotSigned: item['MX_CLIENTE_FIRMA_H'] == '0',
      neg: item['NEG'] as string,
      um: item['UM_H'] as string,
      pallet: item['MX_PALLET_RETORNO'] as string,
      flag_pending:
        item['FLAG_PEDIDO_PEN'] === '1' || item['MX_PENDIENTE_H'] === '1'
          ? '1'
          : '0',
      SummaryOrder: 'Instrucción de guía',
      manualStatus: item['MX_ULTIMO_ESTADO_MANUAL_H']
        ? +String(item['MX_ULTIMO_ESTADO_MANUAL_H'])
        : 0,
      files: ((item['ARCHIVOS'] as unknown[]) ?? []).map((file: Record<string, unknown>) => ({
        id: file['MX_FILES_ID'] as number,
        fileName: file['MX_NOMBRE_ARCHIVO'] as string,
        label: file['MX_ETIQUETA_ARCHIVO'] as string,
        url: file['URL'] as string,
      })),
      operatorComments: ((item['COMENTARIOS_OPERADORES'] as unknown[]) ?? []).map(
        (comment: Record<string, unknown>) => ({
          id: String(comment['MX_COMENTARIO_ID'] ?? ''),
          tkt: String(comment['MX_TKT_H_COM'] ?? ''),
          rut: String(comment['MX_RUT_CONDUCTOR'] ?? ''),
          driverName: String(comment['MX_NOMBRE_CONDUCTOR'] ?? ''),
          workCode: String(comment['MX_CODIGO_PROYE_H'] ?? ''),
          date: String(comment['MX_FECHA_COMENTARIO'] ?? ''),
          comment: String(comment['MX_COMENTARIO'] ?? ''),
        })
      ),
    } as Order;
    return order;
  });
}
